"use client"

import {
  Bone,
  Drop,
  Heartbeat,
  Scan,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { blobMediaUrl } from "@/lib/blob-url"

/** The Figma frame's background rebuilt at 4096px: "21-grey-lake-icm-5k"
 *  from the calm-gallery set with the frame's lotus layer screened over it
 *  at 60%, as Figma blends them, cropped to the frame's window with room
 *  above and below for phones, lifted a little brighter and given a fine
 *  grain so it does not band (Eugene, 02.10). */
const BACKGROUND = blobMediaUrl(
  "/images/marketing/mri-finds/mri-finds-bg-20261002-v5.webp"
)

const FINDS: { icon: PhosphorIcon; n: 1 | 2 | 3 | 4 }[] = [
  { icon: Scan, n: 1 },
  { icon: Heartbeat, n: 2 },
  { icon: Drop, n: 3 },
  { icon: Bone, n: 4 },
]

/**
 * The membership-first full-body MRI page's first section, right under the
 * hero (Eugene, 02.10): a heading and four light cards, as on the Lp(a)
 * page, each a part of you the scan helps you keep an eye on. Worded as
 * what you can check and follow, not as what could go wrong, and promising
 * no finding. No eyebrow, no lede. Copy in `pricingV5`.
 */
export function MriFindsCards() {
  const t = useTranslations("pricingV5")
  return (
    // Full width over a soft, blurred landscape, the cards frosted white
    // with a white edge (Eugene's Figma, 02.10: Landing template 509:5469).
    <section className="relative isolate overflow-hidden pt-14 pb-16 md:pt-[84px] md:pb-24">
      <Image
        src={BACKGROUND}
        alt=""
        fill
        sizes="100vw"
        // A blur bands when compressed hard.
        quality={90}
        className="-z-10 object-cover"
      />
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        {/* One sentence on why the scan is worth it, smaller and narrower
            than a section title (Eugene, 02.10). */}
        <h2 className="type-display-sm-emphasized mx-auto max-w-[734px] text-center text-balance text-neutral-900">
          {t("mriFindsHeading")}
        </h2>
        <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mt-[78px] lg:grid-cols-4">
          {FINDS.map(({ icon: Icon, n }) => (
            <li
              key={n}
              className="rounded-[20px] border border-white bg-white/90 p-8"
            >
              <Icon aria-hidden size={48} className="text-neutral-900" />
              {/* One sentence: its opening words in black, the rest a
                  shade lighter (Eugene, 02.10). */}
              <p className="type-body-xl mt-8 font-medium text-pretty text-neutral-900/50">
                <span className="text-neutral-900">
                  {t(`mriFinds${n}Lead`)}
                </span>{" "}
                {t(`mriFinds${n}Rest`)}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
